<template>
  <div class="relative flex-1">
    <FormField
      ref="dateFormField"
      readonly
      :value="fieldValue"
      :is-active="isActive"
      :is-error="dateErrorDropdown.isErrorDropdownVisible.value"
      :title="$t('search-form.datepicker.date-title')"
      :placeholder="$t('search-form.datepicker.placeholder')"
      @onClick="onShowDropdown"
      @onFocus="onShowDropdown"
    />

    <DropdownErrorButton
      v-if="dateErrorDropdown.isErrorDropdownVisible.value"
      :ref="dateErrorDropdown.errorDropdown.value"
      v-on-click-outside="onClickOutsideDateErrorDropdownHandler"
      class="mt-2"
      @click="onErrorDropdownClick"
    >
      {{ $t('search-form.validations.required-arr') }}
    </DropdownErrorButton>

    <template v-else-if="townFromId && countryToId && isActive">
      <DatepickerStep
        ref="dropdown"
        v-on-click-outside="onClickOutsideHandler"
        :town-from-id="townFromId"
        :country-to-id="countryToId"
        :dep-date="depDate?.from"
        :arr-date="arrDate?.from"
        @onDatesChange="onDatesChange"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { vOnClickOutside } from '@vueuse/components';

  import type { SelectedDates } from '@entities/dates-picker';
  import type { OnOutsideClickHandler } from '@shared/types';

  import { SearchFormField } from '../../../types';
  import { useErrorDropdown } from '../../../model';
  import FormField from '../FormField.vue';
  import DropdownErrorButton from '../DropdownErrorButton.vue';
  import DatepickerStep from './DatepickerStep.vue';

  const props = defineProps<{
    isActive: boolean;
    hasTownsTo: boolean;
    activeFormField?: SearchFormField;
    townFromId?: number;
    countryToId?: number;
    fieldValue: string;
    arrDate?: SelectedDates;
    depDate?: SelectedDates;
  }>();

  const emit = defineEmits<{
    onDatesChange: [dates: SelectedDates[]];
    onResetDates: [];
    onResetActiveField: [];
    onActiveFieldChange: [field: SearchFormField];
  }>();

  const dropdown = ref<HTMLElement | null>(null);
  const dateFormField = ref<HTMLElement | null>(null);
  const nightsCountFormField = ref<HTMLElement | null>(null);

  const dateErrorDropdown = useErrorDropdown();

  const onClickOutsideDateErrorDropdownHandler =
    dateErrorDropdown.createOnClickOutsideErrorDropdownHandler(dateFormField);

  const onClickOutsideHandler: OnOutsideClickHandler = [
    () => {
      if (!props.fieldValue) {
        emit('onResetDates');
      }

      emit('onResetActiveField');
    },
    { ignore: [dateFormField, nightsCountFormField] },
  ];

  function onDatesChange(dates: SelectedDates[]) {
    emit('onDatesChange', dates);
  }

  function onShowDropdown() {
    if (!props.countryToId || !props.hasTownsTo) {
      dateErrorDropdown.showErrorDropdown();
    } else {
      emit('onActiveFieldChange', SearchFormField.DATE_FORM_FIELD);
    }
  }

  function onErrorDropdownClick() {
    dateErrorDropdown.hideErrorDropdown();
    emit('onActiveFieldChange', SearchFormField.ARRIVAL_FORM_FIELD);
  }
</script>
